import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Input,
  Renderer2,
  RendererStyleFlags2,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CoreService } from '@app/@core/services/core.service';
import { environment } from '@env/environment';
import { PaymentService } from '../payment.service';
import {
  ApplePayPurchaseRequest,
  ApplePayStartSessionResponse,
  IApplePayRequest,
  IPaymentInfo,
} from '../payment.types';
import { FunnelAnalyticsService } from '@app/ahoy-analytics/funnel-analytics.service';

@Component({
  selector: 'app-apple-pay',
  templateUrl: './apple-pay.component.html',
  styleUrls: ['./apple-pay.component.scss'],
  standalone: false,
})
export class ApplePayComponent implements AfterViewInit {
  @ViewChild('applePay', { static: false }) but: ElementRef<HTMLDivElement>;
  @Input('paymentDetails') paymentDetails: IPaymentInfo;
  @Input('paymentId') paymentId: string;
  @Input('isLoading') isLoading: boolean = false;

  constructor(
    private coreService: CoreService,
    private renderer: Renderer2,
    private paymentService: PaymentService,
    private funnelAnalytics: FunnelAnalyticsService,
  ) {}

  private destroyRef = inject(DestroyRef);

  ngAfterViewInit() {
    if (window.ApplePaySession) {
      this.initializeButton();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://applepay.cdn-apple.com/jsapi/v1/apple-pay-sdk.js';
    script.integrity =
      'sha384-k62isOxi8JG4rmOzplPz4PrUZJsaPsd3qccKhrtWhcl7M7rmaAEeyTCfCA8iBI7X';
    script.crossOrigin = 'anonymous';
    script.onload = () => this.initializeButton();
    document.body.appendChild(script);
  }

  private initializeButton() {
    if (!window.ApplePaySession) {
      return;
    }
    this.renderer.setAttribute(this.but.nativeElement, 'buttonstyle', 'black');
    this.renderer.setAttribute(this.but.nativeElement, 'type', 'check-out');
    this.renderer.setAttribute(this.but.nativeElement, 'locale', 'en');
    this.renderer.setStyle(
      this.but.nativeElement,
      'display',
      'inline-block',
      RendererStyleFlags2.Important,
    );
    this.renderer.setStyle(
      this.but.nativeElement,
      '-webkit-appearance',
      '-apple-pay-button',
      RendererStyleFlags2.Important,
    );
    this.renderer.setStyle(
      this.but.nativeElement,
      '-apple-pay-button-type',
      'check-out',
      RendererStyleFlags2.Important,
    );
    this.renderer.setStyle(
      this.but.nativeElement,
      'width',
      '100%',
      RendererStyleFlags2.Important,
    );
    this.renderer.setStyle(
      this.but.nativeElement,
      'height',
      '36px',
      RendererStyleFlags2.Important,
    );
    this.renderer.setStyle(
      this.but.nativeElement,
      'padding',
      '0 16px',
      RendererStyleFlags2.Important,
    );
    this.renderer.setStyle(
      this.but.nativeElement,
      'cursor',
      'pointer',
      RendererStyleFlags2.Important,
    );
  }

  onApplePayClick() {
    if (!ApplePaySession) {
      return;
    }
    this.funnelAnalytics.paymentInit(
      'payment',
      'apple_pay',
      this.paymentDetails?.amount,
      this.paymentDetails?.currency,
    );
    // Define ApplePayPaymentRequest
    const request = {
      countryCode: environment.countrySettings.countryNameCode,
      currencyCode:
        this.paymentDetails?.applePayCurrencyCode ||
        environment.countrySettings.currency,
      merchantCapabilities: ['supports3DS', 'supportsCredit', 'supportsDebit'],
      supportedNetworks: ['visa', 'masterCard', 'amex', 'discover', 'mada'],
      total: {
        label: environment.name,
        type: 'final',
        amount: String(this.paymentDetails?.amount) || '0.00',
      },
    };

    // Create ApplePaySession
    const session = new ApplePaySession(3, request);

    session.onvalidatemerchant = (event) => {
      const options = {
        url: event.validationURL,
        paymentReference: this.paymentId,
      };
      this.paymentService
        .getApplePaySession(options)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(
          (res: ApplePayStartSessionResponse) => {
            let mSession = JSON.parse(res.session);
            session.completeMerchantValidation(mSession);
          },
          (err) => {
            this.coreService.showErrorDialog(err);
          },
        );
    };

    session.onpaymentmethodselected = (event) => {
      // Define ApplePayPaymentMethodUpdate based on the selected payment method.
      // No updates or errors are needed, pass an empty object.
      const update = {
        newTotal: {
          label: environment.name,
          type: 'final',
          amount: String(this.paymentDetails?.amount) || '0.00',
        },
      };
      session.completePaymentMethodSelection(update);
    };

    session.onpaymentauthorized = (event) => {
      // Define ApplePayPaymentAuthorizationResult
      const result: ApplePayPurchaseRequest = {
        apple_data: event.payment?.token?.paymentData?.data,
        apple_signature: event.payment?.token?.paymentData?.signature,
        apple_header: {
          apple_transactionId:
            event.payment?.token?.paymentData?.header?.transactionId,
          apple_ephemeralPublicKey:
            event.payment?.token?.paymentData?.header?.ephemeralPublicKey,
          apple_publicKeyHash:
            event.payment?.token?.paymentData?.header?.publicKeyHash,
        },
        apple_paymentMethod: {
          apple_displayName: event.payment?.token?.paymentMethod?.displayName,
          apple_network: event.payment?.token?.paymentMethod?.network,
          apple_type: event.payment?.token?.paymentMethod?.type,
        },
        payment_id: this.paymentId,
      };
      this.paymentService
        .applePayPurchase(result)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(
          (res: IApplePayRequest) => {
            session.completePayment(ApplePaySession.STATUS_SUCCESS);
            window.location.href = res.redirectionUrl;
          },
          (err) => {
            session.completePayment(ApplePaySession.STATUS_FAILURE);
            this.coreService.showErrorDialog(err);
          },
        );
    };

    session.oncancel = (event) => {
      // Payment cancelled by WebKit
    };

    session.begin();
  }
}
